import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState, money } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Wrench, Plus, Trash2, Cog, Hammer, ClipboardCheck, AlertTriangle, Bell,
  UserCheck, RotateCcw, History, Ban, TrendingUp, IndianRupee,
} from "lucide-react";

const empty = { asset_type: "machine", asset_name: "", asset_code: "", serial_no: "", location: "", last_service: "", next_service: "", status: "operational", notes: "" };

export default function Maintenance() {
  const { user } = useAuth();
  const canManage = ["admin", "manager", "store_keeper"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [open, setOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(null);
  const [form, setForm] = useState(empty);
  const [log, setLog] = useState({ description: "", cost: 0, done_by: user?.name || "" });
  const [tab, setTab] = useState("all");
  const [assignFor, setAssignFor] = useState(null);
  const [assignForm, setAssignForm] = useState({ contractor_id: "", note: "" });
  const [histFor, setHistFor] = useState(null);
  const [oooFor, setOooFor] = useState(null);
  const [oooReason, setOooReason] = useState("");

  const load = () => api.get("/maintenance").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/ledgers?type=contractor").then((r) => setContractors(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    try {
      await api.post("/maintenance", form);
      toast.success("Asset added");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addLog = async () => {
    try {
      await api.post(`/maintenance/${logOpen}/log`, { ...log, cost: Number(log.cost) });
      toast.success("Service logged");
      setLogOpen(null); setLog({ description: "", cost: 0, done_by: user?.name || "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (m, status, reason = "") => {
    await api.put(`/maintenance/${m.id}`, { ...m, status, out_of_order_reason: status === "out_of_order" ? reason : "" });
    load();
  };
  const confirmOoo = async () => {
    if (!oooFor) return;
    await setStatus(oooFor, "out_of_order", oooReason);
    toast.success("Marked out of order — flagged for maintenance");
    setOooFor(null); setOooReason("");
  };
  const assign = async () => {
    try {
      const c = contractors.find((x) => x.id === assignForm.contractor_id);
      await api.post(`/maintenance/${assignFor.id}/assign`, {
        action: "issued", contractor_id: assignForm.contractor_id, contractor_name: c?.name || "", note: assignForm.note,
      });
      toast.success("Tool issued to contractor");
      setAssignFor(null); setAssignForm({ contractor_id: "", note: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const returnTool = async (m) => {
    await api.post(`/maintenance/${m.id}/assign`, { action: "returned", note: "" });
    toast.success("Tool marked returned"); load();
  };
  const remove = async (id) => { await api.delete(`/maintenance/${id}`); toast.success("Deleted"); load(); };

  const shown = tab === "all" ? items : items.filter((i) => i.asset_type === tab);
  const tools = items.filter((i) => i.asset_type === "hand_tool");

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueAlerts = items.map((m) => {
    if (m.status !== "operational") return { id: m.id, name: m.asset_name, reason: m.status.replace(/_/g, " ") + (m.out_of_order_reason ? ` — ${m.out_of_order_reason}` : ""), level: m.status === "out_of_order" ? "overdue" : "warn" };
    if (m.next_service) {
      const d = new Date(m.next_service);
      if (!isNaN(d.getTime())) {
        const diff = (d - today) / 86400000;
        if (diff < 0) return { id: m.id, name: m.asset_name, reason: `overdue (was ${m.next_service})`, level: "overdue" };
        if (diff <= 7) return { id: m.id, name: m.asset_name, reason: `due ${m.next_service}`, level: "soon" };
      }
    }
    return null;
  }).filter(Boolean);

  // Insights — repair frequency & spend per asset
  const withCost = items.map((m) => ({ ...m, count: (m.logs || []).length, cost: (m.logs || []).reduce((s, l) => s + (l.cost || 0), 0) }));
  const mostRepaired = [...withCost].filter((m) => m.count > 0).sort((a, b) => b.count - a.count || b.cost - a.cost).slice(0, 5);
  const totalSpend = withCost.reduce((s, m) => s + m.cost, 0);
  const totalRepairs = withCost.reduce((s, m) => s + m.count, 0);

  const STATUS_OPTS = [
    ["operational", "Operational"], ["needs_service", "Needs Service"],
    ["under_repair", "Under Repair"], ["out_of_order", "Out of Order"],
  ];

  return (
    <div className="space-y-8">
      <PageHeader title="Machine & Tool Maintenance" subtitle="Track servicing of machines, and issue hand tools to contractors with a full register." icon={Wrench}
        action={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-asset-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px"><Plus className="h-4 w-4" /> New Asset</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Add Asset</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                    <SelectTrigger data-testid="asset-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="machine">Machine</SelectItem><SelectItem value="hand_tool">Hand Tool</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="asset-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="col-span-2 space-y-2"><Label>Asset name</Label>
                  <Input data-testid="asset-name" value={form.asset_name} onChange={(e) => setForm({ ...form, asset_name: e.target.value })} placeholder="Panel Saw / Cordless Drill" /></div>
                <div className="space-y-2"><Label>Code</Label>
                  <Input value={form.asset_code} onChange={(e) => setForm({ ...form, asset_code: e.target.value })} placeholder="MC-01" /></div>
                <div className="space-y-2"><Label>Serial No.</Label>
                  <Input data-testid="asset-serial" value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} placeholder="SN-99823" /></div>
                <div className="space-y-2"><Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Cutting Section" /></div>
                <div className="space-y-2"><Label>Last service</Label>
                  <Input type="date" value={form.last_service} onChange={(e) => setForm({ ...form, last_service: e.target.value })} /></div>
                <div className="space-y-2"><Label>Next service</Label>
                  <Input type="date" value={form.next_service} onChange={(e) => setForm({ ...form, next_service: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="asset-save" onClick={create} disabled={!form.asset_name}>Add Asset</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      {dueAlerts.length > 0 && (
        <div data-testid="maint-reminders" className="animate-fade-up rounded-md border border-ochre/30 bg-ochre/5 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Bell className="h-4 w-4 text-ochre" />
            <p className="font-heading text-sm font-bold">{dueAlerts.length} maintenance reminder{dueAlerts.length > 1 ? "s" : ""}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {dueAlerts.map((a) => (
              <span key={a.id} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${a.level === "overdue" ? "border-destructive/30 bg-destructive/10 text-destructive" : "border-ochre/40 bg-ochre/10 text-ochre"}`}>
                <AlertTriangle className="h-3 w-3" /> {a.name}: {a.reason}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="rounded-md border-border p-5 shadow-none">
          <div className="flex items-center gap-2 text-muted-foreground"><IndianRupee className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wide">Total maintenance spend</p></div>
          <p data-testid="maint-total-spend" className="mt-2 font-heading text-2xl font-bold">{money(totalSpend)}</p>
          <p className="mt-1 text-xs text-muted-foreground">{totalRepairs} service record{totalRepairs === 1 ? "" : "s"} across {items.length} asset{items.length === 1 ? "" : "s"}</p>
        </Card>
        <Card className="rounded-md border-border p-5 shadow-none lg:col-span-2">
          <div className="mb-3 flex items-center gap-2 text-muted-foreground"><TrendingUp className="h-4 w-4" /><p className="text-xs font-medium uppercase tracking-wide">Most repaired assets</p></div>
          {mostRepaired.length === 0 ? (
            <p className="text-sm text-muted-foreground">No service records yet.</p>
          ) : (
            <div className="space-y-2" data-testid="maint-insights">
              {mostRepaired.map((m) => (
                <div key={m.id} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 font-medium">
                    {m.asset_type === "machine" ? <Cog className="h-3.5 w-3.5 text-primary" /> : <Hammer className="h-3.5 w-3.5 text-ochre" />}
                    {m.asset_name}{m.serial_no ? <span className="text-xs text-muted-foreground">· {m.serial_no}</span> : null}
                  </span>
                  <span className="flex items-center gap-3 text-xs text-muted-foreground">
                    <Badge variant="outline" className="rounded-full">{m.count} repair{m.count === 1 ? "" : "s"}</Badge>
                    <span className="font-semibold text-foreground">{money(m.cost)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {[["all", "All"], ["machine", "Machines"], ["hand_tool", "Hand Tools"]].map(([v, l]) => (
          <button key={v} data-testid={`maint-tab-${v}`} onClick={() => setTab(v)}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${tab === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}>{l}</button>
        ))}
      </div>

      {/* Tools Register (hand tools tab) */}
      {tab === "hand_tool" ? (
        tools.length === 0 ? (
          <EmptyState icon={Hammer} title="No hand tools yet" hint="Add hand tools to issue them to contractors and track their condition." />
        ) : (
          <Card className="overflow-hidden rounded-md border-border shadow-none">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Tool</TableHead><TableHead>Serial No.</TableHead><TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead><TableHead>Repairs / Cost</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tools.map((m) => {
                    const cost = (m.logs || []).reduce((s, l) => s + (l.cost || 0), 0);
                    return (
                      <TableRow key={m.id} data-testid={`tool-row-${m.id}`} className="hover:bg-muted/40 align-top">
                        <TableCell className="font-semibold">
                          {m.asset_name}
                          {m.status === "out_of_order" && m.out_of_order_reason && (
                            <p className="mt-0.5 text-xs font-normal text-destructive">⚠ {m.out_of_order_reason}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{m.serial_no || "—"}</TableCell>
                        <TableCell>
                          {m.assigned_contractor_name
                            ? <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-500/10 text-emerald-600">{m.assigned_contractor_name}</Badge>
                            : <span className="text-xs text-muted-foreground">In store</span>}
                        </TableCell>
                        <TableCell><StatusBadge status={m.status} /></TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{(m.logs || []).length} · {money(cost)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            {canManage && (m.assigned_contractor_id
                              ? <Button size="sm" variant="outline" data-testid={`tool-return-${m.id}`} onClick={() => returnTool(m)} className="h-7 gap-1 text-xs"><RotateCcw className="h-3 w-3" /> Return</Button>
                              : <Button size="sm" variant="outline" data-testid={`tool-assign-${m.id}`} onClick={() => { setAssignFor(m); setAssignForm({ contractor_id: "", note: "" }); }} className="h-7 gap-1 text-xs"><UserCheck className="h-3 w-3" /> Issue</Button>
                            )}
                            <Button size="sm" variant="ghost" data-testid={`tool-history-${m.id}`} onClick={() => setHistFor(m)} className="h-7 gap-1 text-xs"><History className="h-3 w-3" /> History</Button>
                            <Button size="sm" variant="ghost" data-testid={`tool-log-${m.id}`} onClick={() => setLogOpen(m.id)} className="h-7 gap-1 text-xs"><ClipboardCheck className="h-3 w-3" /> Log</Button>
                            {m.status === "out_of_order"
                              ? <Button size="sm" variant="ghost" data-testid={`tool-ok-${m.id}`} onClick={() => setStatus(m, "operational")} className="h-7 text-xs text-sage">Mark OK</Button>
                              : <Button size="sm" variant="ghost" data-testid={`tool-ooo-${m.id}`} onClick={() => { setOooFor(m); setOooReason(m.out_of_order_reason || ""); }} className="h-7 gap-1 text-xs text-destructive"><Ban className="h-3 w-3" /> Out of Order</Button>}
                            {canManage && <Button size="icon" variant="ghost" data-testid={`asset-delete-${m.id}`} onClick={() => remove(m.id)} className="h-7 w-7 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )
      ) : shown.length === 0 ? (
        <EmptyState icon={Wrench} title="No assets tracked" hint="Add machines and hand tools to schedule maintenance." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((m) => (
            <Card key={m.id} data-testid={`asset-card-${m.id}`} className="animate-fade-up rounded-md border-border p-5 shadow-none">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-md ${m.asset_type === "machine" ? "bg-primary/10 text-primary" : "bg-ochre/10 text-ochre"}`}>
                    {m.asset_type === "machine" ? <Cog className="h-5 w-5" /> : <Hammer className="h-5 w-5" />}
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-bold leading-tight">{m.asset_name}</h3>
                    <p className="text-xs text-muted-foreground">{m.asset_code || "—"}{m.serial_no ? ` · SN ${m.serial_no}` : ""} · {m.location || "—"}</p>
                  </div>
                </div>
                <StatusBadge status={m.status} />
              </div>
              <div className="mt-4 space-y-1 text-xs text-muted-foreground">
                {m.asset_type === "hand_tool" && m.assigned_contractor_name && <p className="text-emerald-600">Issued to: {m.assigned_contractor_name}</p>}
                {m.status === "out_of_order" && m.out_of_order_reason && <p className="text-destructive">Out of order: {m.out_of_order_reason}</p>}
                <p>Last service: {m.last_service ? m.last_service.slice(0, 10) : "—"}</p>
                <p>Next due: {m.next_service || "—"}</p>
                <p>{(m.logs || []).length} service record(s)
                  {m.logs?.length > 0 && ` · ${money(m.logs.reduce((s, l) => s + (l.cost || 0), 0))} spent`}</p>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button size="sm" variant="outline" data-testid={`asset-log-${m.id}`} onClick={() => setLogOpen(m.id)} className="gap-1">
                  <ClipboardCheck className="h-3.5 w-3.5" /> Log Service
                </Button>
                {m.status === "out_of_order" ? (
                  <Button size="sm" variant="ghost" data-testid={`asset-ok-${m.id}`} onClick={() => setStatus(m, "operational")} className="text-sage">Mark OK</Button>
                ) : m.status !== "operational" ? (
                  <Button size="sm" variant="ghost" onClick={() => setStatus(m, "operational")} className="text-sage">Mark OK</Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setStatus(m, "needs_service")} className="text-ochre">Flag Service</Button>
                )}
                {canManage && <Button size="icon" variant="ghost" data-testid={`asset-delete-${m.id}`} onClick={() => remove(m.id)}
                  className="ml-auto h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Log Service dialog */}
      <Dialog open={!!logOpen} onOpenChange={(v) => !v && setLogOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Log Service / Repair</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Work done</Label>
              <Textarea data-testid="log-description" value={log.description} onChange={(e) => setLog({ ...log, description: e.target.value })} placeholder="Replaced blade, greased bearings…" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cost ₹</Label>
                <Input type="number" data-testid="log-cost" value={log.cost} onChange={(e) => setLog({ ...log, cost: e.target.value })} /></div>
              <div className="space-y-2"><Label>Done by</Label>
                <Input value={log.done_by} onChange={(e) => setLog({ ...log, done_by: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="log-save" onClick={addLog} disabled={!log.description}>Save Log</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue tool to contractor */}
      <Dialog open={!!assignFor} onOpenChange={(v) => !v && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Issue “{assignFor?.asset_name}” to a contractor</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Contractor</Label>
              <Select value={assignForm.contractor_id} onValueChange={(v) => setAssignForm({ ...assignForm, contractor_id: v })}>
                <SelectTrigger data-testid="assign-contractor"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                <SelectContent>
                  {contractors.length === 0 ? <div className="px-2 py-1.5 text-xs text-muted-foreground">No contractor ledgers found</div>
                    : contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>Note (optional)</Label>
              <Input data-testid="assign-note" value={assignForm.note} onChange={(e) => setAssignForm({ ...assignForm, note: e.target.value })} placeholder="Condition when issued…" /></div>
          </div>
          <DialogFooter><Button data-testid="assign-save" onClick={assign} disabled={!assignForm.contractor_id}>Issue Tool</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Out of order reason */}
      <Dialog open={!!oooFor} onOpenChange={(v) => !v && setOooFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Mark “{oooFor?.asset_name}” Out of Order</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason it isn’t working</Label>
            <Textarea data-testid="ooo-reason" value={oooReason} onChange={(e) => setOooReason(e.target.value)} placeholder="Motor burnt out, chuck broken…" />
            <p className="text-xs text-muted-foreground">This flags the tool in maintenance reminders.</p>
          </div>
          <DialogFooter><Button data-testid="ooo-save" variant="destructive" onClick={confirmOoo} disabled={!oooReason.trim()}>Mark Out of Order</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment history */}
      <Dialog open={!!histFor} onOpenChange={(v) => !v && setHistFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Assignment History — {histFor?.asset_name}</DialogTitle></DialogHeader>
          {(histFor?.assignments || []).length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No assignment records yet.</p>
          ) : (
            <div className="max-h-[50vh] space-y-2 overflow-y-auto">
              {[...(histFor?.assignments || [])].reverse().map((a, i) => (
                <div key={i} data-testid="assign-history-row" className="flex items-start justify-between rounded-md border border-border p-3 text-sm">
                  <div>
                    <p className="font-medium">
                      {a.action === "returned"
                        ? <span className="text-ochre">Returned to store</span>
                        : <span className="text-emerald-600">Issued to {a.contractor_name || "—"}</span>}
                    </p>
                    {a.note && <p className="text-xs text-muted-foreground">{a.note}</p>}
                  </div>
                  <p className="whitespace-nowrap text-xs text-muted-foreground">{(a.date || "").slice(0, 10)} · {a.by}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
